import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { videosAPI, projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Film, Upload, Trash2, Copy, Share2, Mail, Phone, Play, X,
  Loader2, FileVideo, Clock, User, ExternalLink, Download, Link2, ChevronDown, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const VideoCard = ({ video, token, canDelete, onDelete, onShare, onPlay }) => {
  const streamUrl = `/api/videos/${video.id}/stream?auth=${token}`;
  const isReady = video.transcode_status === 'ready';
  const isProcessing = video.transcode_status === 'processing';

  return (
    <div className="card-hud overflow-hidden animate-scale-in group" data-testid={`video-card-${video.id}`}>
      <div className="aspect-video bg-gray-900 relative cursor-pointer" onClick={() => onPlay(video)}>
        <video
          src={streamUrl}
          className="w-full h-full object-contain"
          preload="metadata"
          muted
          onMouseEnter={(e) => e.target.play().catch(() => {})}
          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
            <Play size={24} className="text-navy ml-1" />
          </div>
        </div>
        {isProcessing && (
          <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 bg-black/70 rounded text-[10px] text-amber-400 font-mono">
            <Loader2 size={10} className="animate-spin" /> OTIMIZANDO...
          </div>
        )}
        {isReady && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-black/70 rounded text-[10px] text-green-400 font-mono">
            <Zap size={10} /> HD
          </div>
        )}
      </div>

      <div className="p-3 space-y-2">
        <p className="text-navy text-sm font-medium truncate" title={video.original_filename}>
          {video.original_filename}
        </p>
        <div className="flex items-center gap-3 text-gray-400 text-[10px] font-mono">
          <span className="flex items-center gap-1"><FileVideo size={10} /> {formatSize(video.size)}</span>
          <span className="flex items-center gap-1"><Clock size={10} /> {formatDate(video.created_at)}</span>
          <span className="flex items-center gap-1"><User size={10} /> {video.uploaded_by}</span>
        </div>
        <div className="flex gap-1 pt-1 border-t border-gray-100">
          <Button variant="ghost" size="sm" className="text-gold hover:text-gold hover:bg-gold/10 h-7 px-2 text-[10px] flex-1"
            onClick={() => onShare(video)} data-testid={`share-video-${video.id}`}>
            <Link2 size={12} className="mr-1" /> COMPARTILHAR
          </Button>
          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 h-7 px-2 text-[10px]"
            onClick={() => onPlay(video)} data-testid={`play-video-${video.id}`}>
            <Play size={12} className="mr-1" /> PLAY
          </Button>
          <a href={streamUrl} download className="inline-flex items-center text-gray-400 hover:text-green-600 hover:bg-green-50 h-7 px-2 rounded text-[10px] transition-colors"
            data-testid={`download-video-${video.id}`}>
            <Download size={12} />
          </a>
          {canDelete && (
            <Button variant="ghost" size="icon" className="text-gray-300 hover:text-red-500 hover:bg-red-50 h-7 w-7"
              onClick={() => onDelete(video)} data-testid={`delete-video-${video.id}`}>
              <Trash2 size={12} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const VideosPage = () => {
  const { user, token, isSuperAdmin, isAdmin } = useAuth();
  const canManage = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'supervisor';
  const canUpload = !isSuperAdmin && (user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'leader' || user?.role === 'pilot');
  const isPilot = user?.role === 'pilot';

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [shareVideo, setShareVideo] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await projectsAPI.list();
        const vProjects = res.data.filter(p => p.has_videos);
        setProjects(vProjects);
        if (vProjects.length > 0) {
          const userProject = vProjects.find(p => p.id === user?.project_id);
          setSelectedProject(userProject ? userProject.id : vProjects[0].id);
        }
      } catch (e) { console.error(e); }
    };
    fetchProjects();
  }, [user?.project_id]);

  const fetchVideos = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const res = await videosAPI.list(selectedProject);
      setVideos(res.data);
    } catch (e) { toast.error('Erro ao carregar videos'); }
    finally { setLoading(false); }
  }, [selectedProject]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  // Auto-refresh while any video is processing
  useEffect(() => {
    const hasProcessing = videos.some(v => v.transcode_status === 'processing');
    if (!hasProcessing) return;
    const interval = setInterval(() => {
      if (selectedProject) {
        videosAPI.list(selectedProject).then(res => setVideos(res.data)).catch(() => {});
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [videos, selectedProject]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProject) return;

    const validExts = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v'];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!validExts.includes(ext)) {
      toast.error(`Formato nao suportado. Use: ${validExts.join(', ')}`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await videosAPI.upload(selectedProject, formData, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      });
      if (isPilot) {
        setUploadComplete(true);
        toast.success('Upload concluido com sucesso!');
      } else {
        toast.success('Video enviado! Otimizando para streaming instantaneo...');
      }
      fetchVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (video) => {
    if (!window.confirm(`Excluir "${video.original_filename}"?`)) return;
    try {
      await videosAPI.delete(selectedProject, video.id);
      toast.success('Video excluido!');
      fetchVideos();
    } catch (e) { toast.error('Erro ao excluir video'); }
  };

  const getShareUrl = (video) => `${window.location.origin}/video/${video.share_token}`;

  const copyShareLink = (video) => {
    navigator.clipboard.writeText(getShareUrl(video));
    toast.success('Link copiado!');
  };

  const shareWhatsApp = (video) => {
    const url = encodeURIComponent(getShareUrl(video));
    const text = encodeURIComponent(`Video: ${video.original_filename}`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  const shareEmail = (video) => {
    const subject = encodeURIComponent(`Video RPAS: ${video.original_filename}`);
    const body = encodeURIComponent(`Assista ao video:\n\n${getShareUrl(video)}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const projectName = projects.find(p => p.id === selectedProject)?.name || '';

  return (
    <div className="space-y-6" data-testid="videos-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <Film size={20} className="text-gold" /> VIDEOS DO PROJETO
        </h2>
        <div className="flex items-center gap-3">
          {(isSuperAdmin || projects.length > 1) && (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-[250px] input-hud" data-testid="video-project-select">
                <SelectValue placeholder="Selecione o projeto" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Upload Area - hidden for super_admin, only for user's assigned project */}
      {canUpload && selectedProject && (user?.project_ids?.includes(selectedProject) || user?.project_id === selectedProject) && (
        <div className={`card-hud p-6 border-2 border-dashed transition-all ${uploading ? 'border-gold bg-gold/5' : 'border-gray-300 hover:border-gold/50'}`}
          data-testid="video-upload-zone">
          <input ref={fileInputRef} type="file" className="hidden" accept="video/*" onChange={handleUpload} data-testid="video-file-input" />
          {uploading ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Loader2 size={20} className="text-gold animate-spin" />
                <span className="text-navy font-rajdhani font-semibold uppercase tracking-wider">ENVIANDO VIDEO...</span>
                <span className="text-gold font-mono font-bold">{uploadProgress}%</span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gold rounded-full transition-all duration-300 ease-out" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          ) : (
            <div className="text-center cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center bg-gray-100">
                <Upload size={22} className="text-gray-400" />
              </div>
              <p className="text-navy font-rajdhani font-semibold text-base uppercase tracking-wider mb-1">ENVIAR VIDEO</p>
              <p className="text-gray-500 text-xs">MP4, MOV, AVI, MKV, WebM</p>
            </div>
          )}
        </div>
      )}

      {/* Upload Success Message for Pilot */}
      {isPilot && uploadComplete && (
        <div className="card-hud p-6 border-2 border-green-200 bg-green-50 text-center" data-testid="upload-complete-msg">
          <div className="w-14 h-14 rounded-full bg-green-100 mx-auto mb-3 flex items-center justify-center">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <p className="text-green-800 font-rajdhani font-semibold text-lg uppercase tracking-wider">UPLOAD CONCLUIDO</p>
          <p className="text-green-600 text-sm mt-1">Video enviado com sucesso ao projeto</p>
          <Button className="mt-4 bg-green-600 hover:bg-green-700 text-white text-xs" onClick={() => { setUploadComplete(false); }} data-testid="upload-new-video-btn">
            <Upload size={14} className="mr-1" /> ENVIAR OUTRO VIDEO
          </Button>
        </div>
      )}

      {/* Video Grid - Hidden for Pilot */}
      {!isPilot && (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" />
            </div>
          ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="video-grid">
              {videos.map(video => (
                <VideoCard
                  key={video.id}
                  video={video}
                  token={token}
                  canDelete={canManage}
                  onDelete={handleDelete}
                  onShare={setShareVideo}
                  onPlay={setPlayingVideo}
                />
              ))}
            </div>
          ) : selectedProject ? (
            <div className="card-hud p-8 text-center">
              <Film size={48} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-mono text-sm">Nenhum video enviado</p>
              {canUpload && <p className="text-gray-500 text-xs mt-2">Clique em "Enviar Video" para comecar</p>}
            </div>
          ) : (
            <div className="card-hud p-8 text-center">
              <Film size={48} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-mono text-sm">Nenhum projeto com videos habilitados</p>
            </div>
          )}
        </>
      )}

      {/* Video Player Modal */}
      <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
        <DialogContent className="bg-black border-none max-w-4xl p-0 overflow-hidden">
          <div className="relative">
            <button onClick={() => setPlayingVideo(null)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              data-testid="close-video-player">
              <X size={18} />
            </button>
            {playingVideo && (
              <video
                src={`/api/videos/${playingVideo.id}/stream?auth=${token}`}
                className="w-full max-h-[80vh]"
                controls
                autoPlay
                preload="auto"
                onError={(e) => {
                  console.error('Video error:', e);
                  toast.error('Erro ao carregar video. Tente novamente.');
                }}
                data-testid="video-player"
              />
            )}
          </div>
          {playingVideo && (
            <div className="px-4 py-3 bg-gray-950 flex items-center justify-between">
              <p className="text-white text-sm truncate flex-1">{playingVideo.original_filename}</p>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-gold h-7 px-2 text-[10px]"
                  onClick={() => { setPlayingVideo(null); setShareVideo(playingVideo); }}>
                  <Link2 size={12} className="mr-1" /> COMPARTILHAR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={!!shareVideo} onOpenChange={() => setShareVideo(null)}>
        <DialogContent className="bg-white border border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-navy font-rajdhani uppercase tracking-wider">COMPARTILHAR VIDEO</DialogTitle>
          </DialogHeader>
          {shareVideo && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-navy text-sm font-medium truncate mb-1">{shareVideo.original_filename}</p>
                <p className="text-gray-400 text-xs font-mono">{formatSize(shareVideo.size)} | {formatDate(shareVideo.created_at)}</p>
              </div>
              <div className="bg-gold/5 border border-gold/20 rounded-lg p-3">
                <p className="text-gray-500 text-[10px] font-mono uppercase mb-1">Link compartilhavel</p>
                <p className="text-navy text-xs font-mono break-all" data-testid="share-link-url">{getShareUrl(shareVideo)}</p>
              </div>
              <Button onClick={() => copyShareLink(shareVideo)} className="w-full btn-primary text-xs" data-testid="copy-share-link">
                <Copy size={14} className="mr-2" /> COPIAR LINK
              </Button>
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => shareWhatsApp(shareVideo)}
                  className="bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 rounded-lg h-10 text-xs"
                  data-testid="share-whatsapp">
                  <Phone size={14} className="mr-1" /> WhatsApp
                </Button>
                <Button onClick={() => shareEmail(shareVideo)}
                  className="bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 rounded-lg h-10 text-xs"
                  data-testid="share-email">
                  <Mail size={14} className="mr-1" /> E-mail
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideosPage;
